import { CircleCohortInfo } from '@calm/evexia-web-sdk';
import useAuth from '@rhg/libs/auth0/use-auth';
import { queryClient, queryKeys } from '@rhg/libs/react-query';
import {
    CIRCLE_JWT_EXPIRATION_BACKSTOP_MS,
    CIRCLE_JWT_EXPIRATION_BUFFER_MS,
    getCircleJwtRemainingMs,
} from '@rhg/utils/circle';
import {
    createContext,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { CircleAuthState } from './types';

export const CircleAuthContext = createContext<CircleAuthState>({
    authorizationJWTs: {},
    cohortInfos: {},
    watchCircle: () => () => {},
});

export function CircleAuthProviderInner({
    children,
    isAuthenticated = true,
}: {
    children: ReactNode | ReactNode[];
    isAuthenticated?: boolean;
}) {
    const [authorizationJWTs, setAuthorizationJWTs] = useState<
        Record<string, string | undefined>
    >(() => ({}));
    const [cohortInfos, setCohortInfos] = useState<
        Record<string, CircleCohortInfo | undefined>
    >(() => ({}));
    const [isExpiredFlags, setIsExpiredFlags] = useState<
        Record<string, boolean | undefined>
    >({});

    // Per-circleId timeout IDs for proactive refresh and hard-expiry backstop.
    const jwtRefetchTimeoutIds = useRef<
        Record<string, ReturnType<typeof setTimeout> | undefined>
    >({});
    const jwtBackstopTimeoutIds = useRef<
        Record<string, ReturnType<typeof setTimeout> | undefined>
    >({});

    // Tracks the JWT currently managed by timers per circleId. Used to skip
    // timer re-arming when the same JWT is seen again (e.g. duplicate cache
    // events), preventing a rapid-fire loop when callbackTime <= 0.
    const currentJWTsRef = useRef<Record<number, string | undefined>>({});

    // Ref-count of active watchers per circleId. The query cache subscription
    // for a circleId is only active while at least one component is watching it.
    const watchRefCounts = useRef<Record<number, number>>({});
    // Unsubscribe functions for query cache subscriptions, keyed by circleId.
    const queryWatchUnsubscribers = useRef<Record<number, () => void>>({});

    const isAuthenticatedRef = useRef(isAuthenticated);
    useEffect(() => {
        isAuthenticatedRef.current = isAuthenticated;
    }, [isAuthenticated]);

    useEffect(() => {
        if (!isAuthenticated) {
            Object.values(jwtRefetchTimeoutIds.current).forEach(clearTimeout);
            jwtRefetchTimeoutIds.current = {};
            Object.values(jwtBackstopTimeoutIds.current).forEach(clearTimeout);
            jwtBackstopTimeoutIds.current = {};
            currentJWTsRef.current = {};
        }
    }, [isAuthenticated]);

    // Clear all timers on provider unmount to avoid open handles.
    useEffect(() => {
        const refetchTimeoutIds = jwtRefetchTimeoutIds.current;
        const backstopTimeoutIds = jwtBackstopTimeoutIds.current;
        const watchUnsubscribers = queryWatchUnsubscribers.current;
        return () => {
            Object.values(refetchTimeoutIds).forEach(clearTimeout);
            Object.values(backstopTimeoutIds).forEach(clearTimeout);
            Object.values(watchUnsubscribers).forEach((unsub) => unsub());
        };
    }, []);

    /**
     * Internal: stores a JWT and cohort info for a circleId and arms two timers:
     *
     * 1. Refetch timer — fires the expirationCallback 30 seconds before the JWT
     *    expires, giving the app time to fetch a fresh token before the current
     *    one becomes invalid.
     *
     * 2. Backstop timer — fires ~1 second before hard expiry and marks the JWT
     *    as expired in local state, causing downstream consumers to see `undefined`
     *    for that circleId. This prevents stale tokens from being used if the
     *    proactive refresh didn't happen in time (e.g. the device was offline).
     *
     * If the JWT is unchanged and a timer is already armed, skips re-arming to
     * prevent a rapid-fire loop when duplicate cache events arrive for a
     * near-expired token (callbackTime <= 0 would fire the callback immediately,
     * triggering another refetch, which triggers another cache event, and so on).
     */
    const setAuthorizationJWT = useCallback(
        (
            circleId: number,
            jwt: string | undefined,
            cohortInfo: CircleCohortInfo | undefined,
            expirationCallback: () => void
        ) => {
            const timeTilExpiry = getCircleJwtRemainingMs(jwt);
            // A token already at/past the hard-expiry threshold is dead on
            // arrival. Mask it in the same state batch — waiting for the 0ms
            // backstop timer leaves a one-render window where JWT-gated
            // queries see a "valid" token, fire, and 403 in a burst.
            const isAlreadyExpired =
                timeTilExpiry !== undefined &&
                timeTilExpiry <= CIRCLE_JWT_EXPIRATION_BACKSTOP_MS;

            setAuthorizationJWTs((prev) => {
                if (prev[circleId] === jwt) return prev;
                return { ...prev, [circleId]: jwt };
            });
            setCohortInfos((prev) => {
                if (prev[circleId] === cohortInfo) return prev;
                return { ...prev, [circleId]: cohortInfo };
            });
            if (jwt) {
                setIsExpiredFlags((prev) => {
                    if (!!prev[circleId] === isAlreadyExpired) return prev;
                    return { ...prev, [circleId]: isAlreadyExpired };
                });
            }

            const jwtChanged = currentJWTsRef.current[circleId] !== jwt;
            currentJWTsRef.current[circleId] = jwt;

            // Same JWT and timer already armed — skip re-arming to avoid the
            // duplicate-event rapid-fire loop described above.
            if (!jwtChanged && jwtRefetchTimeoutIds.current[circleId]) {
                return;
            }

            // Clear any previously armed timers for this circle.
            if (jwtRefetchTimeoutIds.current[circleId]) {
                clearTimeout(jwtRefetchTimeoutIds.current[circleId]);
                delete jwtRefetchTimeoutIds.current[circleId];
            }
            if (jwtBackstopTimeoutIds.current[circleId]) {
                clearTimeout(jwtBackstopTimeoutIds.current[circleId]);
                delete jwtBackstopTimeoutIds.current[circleId];
            }

            if (!timeTilExpiry) return;

            const callbackTime =
                timeTilExpiry - CIRCLE_JWT_EXPIRATION_BUFFER_MS;
            // Deliberately NOT cleared after firing: the lingering timeout id
            // is what trips the skip-re-arm guard above when the refetch
            // returns the same (still-expired) JWT. Deleting it inside the
            // callback would re-arm a 0ms timer on every refetch and revive
            // the fetch storm (EVE-5971).
            jwtRefetchTimeoutIds.current[circleId] = setTimeout(
                expirationCallback,
                callbackTime <= 0 ? 0 : callbackTime
            );

            // Already masked synchronously above — no backstop needed.
            if (isAlreadyExpired) return;

            const hardExpirationTime =
                timeTilExpiry - CIRCLE_JWT_EXPIRATION_BACKSTOP_MS;
            jwtBackstopTimeoutIds.current[circleId] = setTimeout(
                () => {
                    setIsExpiredFlags((prev) => {
                        if (prev[circleId]) return prev;
                        return { ...prev, [circleId]: true };
                    });
                },
                hardExpirationTime <= 0 ? 0 : hardExpirationTime
            );
        },
        []
    );

    /**
     * Register a circleId for automatic JWT lifecycle management.
     *
     * The first call for a given circleId:
     *   1. Checks the React Query cache for existing data and syncs it immediately.
     *   2. Subscribes to future cache updates for that circleId.
     *
     * Subsequent calls for the same circleId increment a ref-count and return
     * immediately — only one cache subscription is ever active per circleId,
     * regardless of how many components call useCircle with the same ID.
     *
     * Returns a cleanup function. Call it from useEffect cleanup so the
     * subscription is torn down when the last watcher unmounts.
     */
    const watchCircle = useCallback(
        (circleId: number): (() => void) => {
            if (circleId <= 0) return () => {};

            watchRefCounts.current[circleId] =
                (watchRefCounts.current[circleId] ?? 0) + 1;

            if (watchRefCounts.current[circleId] === 1) {
                // Build the expiration callback once — it always re-fetches this
                // circleId's query, which will call setAuthorizationJWT again with
                // the fresh token via the cache subscription below.
                const expirationCallback = () => {
                    if (!isAuthenticatedRef.current) return;
                    void queryClient.refetchQueries({
                        queryKey: [queryKeys.circleById, circleId],
                    });
                };

                // Sync any data that is already in the cache (e.g. a sibling
                // component fetched this circleId before we mounted).
                const existingData = queryClient.getQueryData<{
                    authorizationJWT?: string;
                    cohortInfo?: CircleCohortInfo;
                }>([queryKeys.circleById, circleId]);
                if (existingData) {
                    setAuthorizationJWT(
                        circleId,
                        existingData.authorizationJWT,
                        existingData.cohortInfo,
                        expirationCallback
                    );
                }

                // Subscribe to future updates so token refreshes are picked up
                // automatically without any component having to call setAuthorizationJWT.
                const unsubscribe = queryClient
                    .getQueryCache()
                    .subscribe((event) => {
                        const queryKey = event.query
                            .queryKey as readonly unknown[];
                        if (
                            event.type === 'updated' &&
                            event.action.type === 'success' &&
                            queryKey[0] === queryKeys.circleById &&
                            queryKey[1] === circleId &&
                            event.query.state.data
                        ) {
                            const data = event.query.state.data as {
                                authorizationJWT?: string;
                                cohortInfo?: CircleCohortInfo;
                            };
                            setAuthorizationJWT(
                                circleId,
                                data.authorizationJWT,
                                data.cohortInfo,
                                expirationCallback
                            );
                        }
                    });
                queryWatchUnsubscribers.current[circleId] = unsubscribe;
            }

            return () => {
                watchRefCounts.current[circleId]--;
                if (watchRefCounts.current[circleId] === 0) {
                    queryWatchUnsubscribers.current[circleId]?.();
                    delete queryWatchUnsubscribers.current[circleId];
                    delete watchRefCounts.current[circleId];
                    // Intentionally do NOT clear the JWT timers here. If the
                    // refetch timer fires while no component is watching, it
                    // refreshes the token in the query cache so that the next
                    // subscriber gets a fresh JWT immediately on re-subscribe
                    // (via the cache sync at the top of watchCircle). Provider-
                    // level teardown in the useEffect above handles timer cleanup
                    // on unmount.
                }
            };
        },
        [setAuthorizationJWT]
    );

    const unexpiredAuthorizationJWTs = useMemo(() => {
        const result: Record<string, string | undefined> = {};
        for (const [circleId, jwt] of Object.entries(authorizationJWTs)) {
            result[circleId] = isExpiredFlags[circleId] ? undefined : jwt;
        }
        return result;
    }, [authorizationJWTs, isExpiredFlags]);

    const unexpiredCohortInfos = useMemo(() => {
        const result: Record<string, CircleCohortInfo | undefined> = {};
        for (const [circleId, cohortInfo] of Object.entries(cohortInfos)) {
            result[circleId] = isExpiredFlags[circleId]
                ? undefined
                : cohortInfo;
        }
        return result;
    }, [cohortInfos, isExpiredFlags]);

    return (
        <CircleAuthContext.Provider
            value={{
                authorizationJWTs: unexpiredAuthorizationJWTs,
                cohortInfos: unexpiredCohortInfos,
                watchCircle,
            }}
        >
            {children}
        </CircleAuthContext.Provider>
    );
}

export function CircleAuthProvider({
    children,
}: {
    children: ReactNode | ReactNode[];
}) {
    const { isAuthenticated } = useAuth();
    return (
        <CircleAuthProviderInner isAuthenticated={isAuthenticated}>
            {children}
        </CircleAuthProviderInner>
    );
}
